import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import debounce from 'lodash/debounce';
import { TextAreaField } from '@kaizen/components';
import request from 'ca-request';
import notifySavingError from '../helpers/notifySavingError';
import ValidationError from '../ValidationError/ValidationError';
import styles from './FreeTextQuestion.scss';
import Submission from '../Submission/Submission';

type Props = {
  questionId: string;
  questionText: string;
  questionMandatory: boolean;
  existingComment: string;
  requestURL: string;
  commentMaxLength: number;
  uiText: {
    savingError: string;
    mandatoryError: string;
  };
};

const FreeTextQuestion = (props: Props) => {
  const { questionId, questionText, questionMandatory, uiText } = props;
  const [comment, setComment] = useState<string>(props.existingComment);
  const [validationError, setValidationError] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textAreaId = `free-text-${props.questionId}`;
  const questionLabel = questionMandatory ? `${questionText} *` : questionText;
  const validationErrorId = `${questionId}-error`;

  // Without having a React container around the Capture form yet, we're listening for a custom event
  // sent from app/assets/javascripts/response.js to signal that submit has been pressed and we want to show validation errors.
  useEffect(() => {
    const checkAndSetError = (_event: any, questionIds: string[]) => {
      const answered = questionIds.indexOf(questionId) === -1;
      setValidationError(!answered);
    };
    if (questionMandatory) {
      $(document).on('promptUnanswered', checkAndSetError);
    }
    return () => {
      $(document).off('promptUnanswered', checkAndSetError);
    };
  }, [questionId, questionMandatory]);

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
    debouncedSave(event.target.value);

    if (event.target.value) {
      setValidationError(false);
    }
  };

  const debouncedSave = useRef(
    debounce(
      (nextValue: string) => {
        Submission.enqueue(() => {
          return request
            .put(props.requestURL, {
              body: JSON.stringify({
                text_value: nextValue,
              }),
            })
            .catch((error) => {
              notifySavingError(
                error,
                props.requestURL,
                'free text save request',
                uiText.savingError
              );
            });
        });

        // Send back to jQuery for some adjustments to the .question container that we can't make here yet
        $(document).trigger('reactAnswerChange', {
          container: containerRef.current,
          answered: !!nextValue,
        });
      },
      1000,
      { maxWait: 10000 }
    )
  ).current;

  const onBlur = () => debouncedSave.flush();

  return (
    <div className={styles.container} ref={containerRef}>
      <TextAreaField
        id={textAreaId}
        name={textAreaId}
        labelText={questionLabel}
        rows={4}
        value={comment}
        onChange={onChange}
        onBlur={onBlur}
        variant="prominent"
        maxLength={props.commentMaxLength}
        aria-describedby={validationError ? validationErrorId : undefined}
        data-focus-to-on-navigate={true}
        required={questionMandatory}
        autogrow
        data-automation-id={`free-text-${questionId}-field-textarea`}
        inline
        // This aria-label _shouldn't_ be required, but there's something weird going on.
        // Windows/Chromium is having trouble determining the accessible name from the label in this environment.
        // See https://cultureamp.slack.com/archives/C0405G8EJ48/p1696896101605489 for more information.
        aria-label={questionLabel}
      />
      {validationError && (
        <ValidationError
          id={validationErrorId}
          uiText={{ message: uiText.mandatoryError }}
        />
      )}
    </div>
  );
};

export default FreeTextQuestion;
